import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LocalStorageUtil } from '../utils';
import { StorageConstant } from '@shared/constants';

type ColumnStateKey = { key: string; order: number };

type ColumnState = {
  name: string;
  /** key -> label suffix. Consumers pass a string enum, which satisfies this shape. */
  keys: Record<string, string>;
  state: ColumnStateKey[];
  /**
   * Mandatory columns for this list: not removable, not reorderable (FR-037).
   *
   * Carried on the state rather than as a panel @Input because the panel is a single instance
   * living in the layout, so it has no per-page binding site — this is the only channel that is
   * already per-list. Absent for every existing list, which is why they are unaffected.
   */
  lockedKeys?: string[];
  /** The arrangement this list's Reset restores. Absent => today's no-op Reset behaviour. */
  defaultState?: ColumnStateKey[];

  /**
   * Labels to render VERBATIM, keyed by column key.
   *
   * A column listed here skips `'Reports.' + suffix | translate` entirely. That is required for a
   * column whose name is data the user typed - a customer dynamic field - because there is no
   * translation key to look up, so the lookup would render the missing key itself.
   *
   * Absent for every pre-existing list, so they all stay fully translated.
   */
  rawLabels?: Record<string, string>;

  /**
   * Hides the "Show dynamic fields" section for lists that do not consume it.
   *
   * Opt-OUT on purpose: absent means shown, so every existing list keeps the section without changing a
   * line. Only a list that manages its dynamic fields as ordinary columns sets this, where the separate
   * section would be redundant and — because nothing listens to the toggle there — inert.
   */
  hideDynamicFieldsToggle?: boolean;

  /** i18n key for the panel subtitle. Absent => the shipment-list wording. */
  subtitleKey?: string;
};

@Injectable({
  providedIn: 'root',
})
export class ColumnOptionsService {
  state$ = new BehaviorSubject<ColumnState>({} as ColumnState);
  drawer$ = new Subject();
  private dynamicFieldsToggle = new BehaviorSubject<boolean>(LocalStorageUtil.getItem(StorageConstant.HAS_DYNAMIC_FIELDS));
  dynamicFieldsToggle$ = this.dynamicFieldsToggle.asObservable();

  private dynamicColumnsSubject = new BehaviorSubject<string[]>([]);
  dynamicColumns$ = this.dynamicColumnsSubject.asObservable();
  private storage: { [k: string]: ColumnState } = {};

  /**
   * Server-side persistence, registered per list by the list itself.
   *
   * Registration IS the opt-in, and the opt-in is the entire safety mechanism (FR-041): a list with
   * no persister — the four shipment-list variants and the filter controls — keeps its existing
   * localStorage-only behaviour, and nothing here can upload or overwrite its arrangement.
   *
   * A callback rather than an endpoint of its own, because each list's record shape is its own
   * business. It also keeps the direction of dependency right: the feature reaches into `shared/`,
   * never the reverse (Principle I).
   */
  private readonly persisters = new Map<string, () => Observable<unknown>>();

  constructor() {
    this.storage = LocalStorageUtil.getItem(StorageConstant.COLUMN_OPTIONS_KEY) || {};
    // No network call here on purpose: app bootstrap must stay request-free, so no list gains a
    // startup request because of this feature. A list that persists loads its own record in its
    // own init.
  }

  /** Opts a list into server-side persistence. Call from the owning feature's init. */
  registerPersister(name: string, persist: () => Observable<unknown>): void {
    this.persisters.set(name, persist);
  }

  /** Drops the registration, so a destroyed feature service is not retained or called. */
  unregisterPersister(name: string): void {
    this.persisters.delete(name);
  }

  set state({
    name,
    keys,
    state,
    lockedKeys,
    defaultState,
    hideDynamicFieldsToggle,
    rawLabels,
    subtitleKey,
  }: Partial<ColumnState> & { name: string }) {
    const currentColOptions = this.storage[name];
    const oldState = currentColOptions?.state || [];
    const newState = state && state.length ? state : oldState;

    const oldKeys = currentColOptions?.keys;
    const newKeys = keys ? keys : oldKeys;

    const newLockedKeys = lockedKeys ?? currentColOptions?.lockedKeys;
    const newDefaultState = defaultState ?? currentColOptions?.defaultState;
    const newHideToggle = hideDynamicFieldsToggle ?? currentColOptions?.hideDynamicFieldsToggle;
    const newRawLabels = rawLabels ?? currentColOptions?.rawLabels;
    const newSubtitleKey = subtitleKey ?? currentColOptions?.subtitleKey;

    this.storage = {
      ...this.storage,
      [name]: {
        name,
        keys: newKeys,
        state: newState,
        lockedKeys: newLockedKeys,
        defaultState: newDefaultState,
        hideDynamicFieldsToggle: newHideToggle,
        rawLabels: newRawLabels,
        subtitleKey: newSubtitleKey,
      },
    };

    LocalStorageUtil.setItem(StorageConstant.COLUMN_OPTIONS_KEY, this.storage);

    this.state$.next({
      name,
      keys,
      state: newState,
      lockedKeys: newLockedKeys,
      defaultState: newDefaultState,
      hideDynamicFieldsToggle: newHideToggle,
      rawLabels: newRawLabels,
      subtitleKey: newSubtitleKey,
    });
  }

  getStateByName(name: string): ColumnState {
    return this.storage[name];
  }

  /**
   * Persists ONE list's arrangement — the one being applied.
   *
   * Scoped to a single list deliberately. Persisting everything registered would upload another
   * list's arrangement as a side effect of this one being applied, which FR-041 forbids. A list with
   * no persister is a no-op, so the panel can call this unconditionally.
   */
  saveStorage(name: string): Observable<unknown> {
    const persist = this.persisters.get(name);
    return persist ? persist().pipe(catchError(() => of(null))) : of(null);
  }

  toggleDrawer(shown?: boolean): void {
    this.drawer$.next(shown);
  }

  toggleDynamicFields(value: boolean): void {
    this.dynamicFieldsToggle.next(value);
  }

  setDynamicColumns(columns: string[]): void {
    this.dynamicColumnsSubject.next(columns);
  }

  get isDynamicFieldsEnabled(): boolean {
    return !!this.dynamicFieldsToggle.value;
  }

  get dynamicColumns(): string[] {
    return this.dynamicColumnsSubject.value;
  }
}
